/** Capture-slot definitions used on the loan application form. */
export const LOAN_DOCUMENT_SLOTS = [
  { key: 'businessPlan', label: 'Business Plan/Proposal', category: 'BUSINESS_PLAN', multiple: false },
  { key: 'legalStatusDocuments', label: 'Legal Status Documents', category: 'OTHER', multiple: false },
  { key: 'companyResolution', label: 'Company Resolution', category: 'OTHER', multiple: false },
  { key: 'operatingLicense', label: 'Operating License', category: 'OTHER', multiple: false },
  { key: 'proofOfPremises', label: 'Proof of Premises', category: 'OTHER', multiple: false },
  { key: 'proofOfCollateral', label: 'Proof of Collateral/Security', category: 'OTHER', multiple: false },
  { key: 'proformaInvoices', label: 'Pro-forma Invoices', category: 'OTHER', multiple: false },
  { key: 'passportPhotos', label: 'Passport Photos', category: 'IDENTIFICATION', multiple: false },
  { key: 'proofOfEquityContribution', label: 'Proof of Equity Contribution', category: 'OTHER', multiple: false },
  { key: 'financialStatements', label: 'Financial Statements', category: 'FINANCIAL_STATEMENTS', multiple: true },
  { key: 'bankStatements6Months', label: 'Bank Statements (6 months)', category: 'BANK_STATEMENTS', multiple: true },
  { key: 'directorsKYC', label: "Director's ID/Passport/Proof of Residence", category: 'IDENTIFICATION', multiple: true },
] as const;

export type LoanDocumentSlotKey = (typeof LOAN_DOCUMENT_SLOTS)[number]['key'];

export type DocumentUploadItem = {
  file: File;
  fieldName: LoanDocumentSlotKey | string;
  documentType: string;
};

export type LoanAttachmentsState = {
  businessPlan: File | null;
  legalStatusDocuments: File | null;
  companyResolution: File | null;
  financialStatements: File[];
  bankStatements6Months: File[];
  operatingLicense: File | null;
  proofOfPremises: File | null;
  proofOfCollateral: File | null;
  proformaInvoices: File | null;
  directorsKYC: File[];
  passportPhotos: File | null;
  proofOfEquityContribution: File | null;
};

const SLOT_BY_KEY = Object.fromEntries(
  LOAN_DOCUMENT_SLOTS.map((slot) => [slot.key, slot])
) as Record<string, (typeof LOAN_DOCUMENT_SLOTS)[number]>;

export function emptyLoanAttachments(): LoanAttachmentsState {
  return {
    businessPlan: null,
    legalStatusDocuments: null,
    companyResolution: null,
    financialStatements: [],
    bankStatements6Months: [],
    operatingLicense: null,
    proofOfPremises: null,
    proofOfCollateral: null,
    proformaInvoices: null,
    directorsKYC: [],
    passportPhotos: null,
    proofOfEquityContribution: null,
  };
}

/** Flatten attachment state into upload items that carry the capture-slot label. */
export function collectDocumentUploadItems(
  attachments: LoanAttachmentsState
): DocumentUploadItem[] {
  const items: DocumentUploadItem[] = [];

  for (const slot of LOAN_DOCUMENT_SLOTS) {
    const value = attachments[slot.key as keyof LoanAttachmentsState];
    if (slot.multiple) {
      const files = (value as File[]) || [];
      files.forEach((file) => {
        items.push({
          file,
          fieldName: slot.key,
          documentType: slot.label,
        });
      });
    } else if (value) {
      items.push({
        file: value as File,
        fieldName: slot.key,
        documentType: slot.label,
      });
    }
  }

  return items;
}

export function getDocumentSlot(fieldName: string) {
  return SLOT_BY_KEY[fieldName];
}
